/*
 * File: statsApi.ts
 *
 * statusApi: RTK Query APIs for the status process.
 *
 */

import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { PageContent } from "../types";

import { AxiosError } from "axios";

import {
  buildStrippedJsonFromValues,
  initAndGetSeonPayload,
  transformAxiosToFetchBaseQueryErrorWithLocationRedirect,
} from "../utils";
import {
  apiOnboardingKeyName,
  apiOnboardingKeyValue,
  endpoints,
  getStatusFetcher,
} from "./ApiConfig";

export const statusApi = createApi({
  reducerPath: "statusApi",
  baseQuery: fetchBaseQuery({
    baseUrl: endpoints.status,
    prepareHeaders: headers => {
      if (apiOnboardingKeyName && apiOnboardingKeyValue) {
        headers.set(apiOnboardingKeyName, apiOnboardingKeyValue);
      }
      return headers;
    },
  }),
  tagTypes: ["Status"],
  endpoints: builder => ({
    statusStart: builder.mutation<PageContent, { queryString: string }>({
      async queryFn() {
        try {
          await initAndGetSeonPayload();
          const res = await getStatusFetcher().get("", {
            withCredentials: true,
          });
          return { data: res?.data };
        } catch (e) {
          const error = transformAxiosToFetchBaseQueryErrorWithLocationRedirect(
            e as AxiosError,
            true,
          );
          return { error };
        }
      },
    }),
    statusNext: builder.mutation<
      PageContent,
      {
        stepId?: number;
        data: PageContent;
        values: Record<string, unknown>;
      }
    >({
      async queryFn({ data, values }) {
        const parsedData = buildStrippedJsonFromValues({
          pageContent: data,
          values,
          filterByVisibility: true,
        });
        try {
          const seonPayload = await initAndGetSeonPayload();
          const res = await getStatusFetcher().patch("next", {
            data: parsedData,
            headers: { seonPayload },
          });
          return { data: res?.data };
        } catch (e) {
          const error = transformAxiosToFetchBaseQueryErrorWithLocationRedirect(
            e as AxiosError,
          );
          return { error };
        }
      },
    }),
  }),
});

export const { useStatusStartMutation, useStatusNextMutation } = statusApi;

export const allStatusFulfilledMatchers = [
  statusApi.endpoints.statusStart.matchFulfilled,
  statusApi.endpoints.statusNext.matchFulfilled,
];

export const allStatusRejectedMatchers = [
  statusApi.endpoints.statusStart.matchRejected,
  statusApi.endpoints.statusNext.matchRejected,
];

export const allStatusPendingMatchers = [
  statusApi.endpoints.statusStart.matchPending,
  statusApi.endpoints.statusNext.matchPending,
];
